import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const KEY = "wekit.display_name";
const EVENT = "wekit:display-name";

/** Broadcast a freshly saved name so every mounted screen updates instantly. */
export function publishDisplayName(name: string | null) {
  try {
    if (name) localStorage.setItem(KEY, name);
    else localStorage.removeItem(KEY);
  } catch {
    // Private-mode browsers: the in-memory event below still works.
  }
  window.dispatchEvent(new CustomEvent(EVENT, { detail: name }));
}

function cached(): string | null {
  try {
    return localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

/**
 * Single source of truth for the learner's name across the app.
 * Profile (`profiles.full_name`) wins over auth metadata, which wins over the
 * email slug — so updating the profile renames you everywhere, immediately.
 */
export function useDisplayName(fallback = "Explorer") {
  const { user } = useAuth();
  const userId = user?.id;
  const metaName = ((user?.user_metadata?.full_name as string) ?? "").trim();
  const emailSlug = user?.email?.split("@")[0] ?? "";

  const [name, setName] = useState<string | null>(() => cached());

  useEffect(() => {
    const onEvent = (e: Event) => setName((e as CustomEvent).detail ?? null);
    window.addEventListener(EVENT, onEvent);
    return () => window.removeEventListener(EVENT, onEvent);
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!userId) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("full_name, email")
        .eq("id", userId)
        .maybeSingle();
      if (cancelled) return;
      const resolved =
        (data?.full_name ?? "").trim() ||
        metaName ||
        (data?.email ?? "").split("@")[0] ||
        emailSlug ||
        null;
      setName(resolved);
      publishDisplayName(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, metaName, emailSlug]);

  const refresh = useCallback((next: string | null) => publishDisplayName(next), []);

  const display = (name || metaName || emailSlug || fallback).trim();
  return { name: display, firstName: display.split(" ")[0], refresh };
}
